import Card from "@mui/material/Card";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import useTutorData from "Views/people/tutor/data/tutorTableData";
import { useState } from "react";
import ArgonInput from "components/ArgonInput";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import { height, width } from "@mui/system";
import api from "utils/api";
import { toast } from "react-toastify";
import PaginationBar from "components/PaginationBar/PaginationBar";
import ArgonButton from "components/ArgonButton";
import CsvBulkUploadDialog from "components/CsvBulkUploadDialog";
import UploadFileIcon from "@mui/icons-material/UploadFile";

function Tables() {
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedTutorId, setSelectedTutorId] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState("All");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [bulkUploadDialog, setBulkUploadDialog] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const handleDeleteClick = (tutorId) => {
    console.log("🗑️ Delete clicked for tutor:", tutorId);
    setSelectedTutorId(tutorId);
    setDeleteDialog(true);
  };

  const handleCloseDialog = () => {
    console.log("❌ Dialog closed");
    setDeleteDialog(false);
    setSelectedTutorId(null);
  };

  // Use the custom hook and pass approvalStatus
  const { columns, rows, counts, refetchTutors, loading } = useTutorData(
    handleDeleteClick,
    approvalStatus
  );

  // Pagination handlers
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  // Calculate paginated rows
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  // Filter rows based on search query
  const filteredRows = rows.filter((row) => {
    if (!searchQuery) return true;

    const searchLower = searchQuery.toLowerCase();

    // Search in name (from profile column)
    const name = row.profile?.props?.name || "";
    if (String(name).toLowerCase().includes(searchLower)) return true;

    // Search in email (from profile column)
    const email = row.profile?.props?.email || "";
    if (String(email).toLowerCase().includes(searchLower)) return true;

    // Search in phone number
    const phone = row.MobileNo?.props?.children || "";
    if (String(phone).toLowerCase().includes(searchLower)) return true;

    // Search in area/city
    const area = row["Area/city"]?.props?.children || "";
    if (String(area).toLowerCase().includes(searchLower)) return true;

    // Search in status
    const status = row.status?.props?.badgeContent || "";
    if (String(status).toLowerCase().includes(searchLower)) return true;

    return false;
  });

  const paginatedRows = filteredRows.slice(startIndex, endIndex);

  const handleDeleteConfirm = async () => {
    console.log("✅ Delete confirmed for tutor:", selectedTutorId);
    if (!selectedTutorId) {
      console.error("⚠️ No tutor ID selected!");
      return;
    }

    try {
      setDeleteLoading(true);
      console.log("📤 Sending delete request to API...");
      const response = await api.put(`/tutor/${selectedTutorId}`, {
        isDeleted: true,
      });

      console.log("📥 API Response:", response.data);

      if (response.data.success) {
        toast.success("Tutor deleted successfully!");
        handleCloseDialog();
        // Refresh the tutor list
        console.log("🔄 Refreshing tutor list...");
        refetchTutors();
      } else {
        toast.error("Failed to delete tutor: " + (response.data.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Error deleting tutor:", error);
      const errorMessage =
        error.response?.data?.message || error.message || "Failed to delete tutor";
      toast.error("Error: " + errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Tab click handler
  const handleTabChange = (status) => {
    setApprovalStatus(status);
    setCurrentPage(1); // Reset to first page when changing tabs
    setSearchQuery(""); // Reset search when changing tabs
  };

  // Search handler
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Tab data with status mapping
  const tabsData = [
    { label: "All", count: counts.All, color: "#2027e1ff", status: "All" },
    { label: "Pending", count: counts.PENDING, color: "#ec3c10ff", status: "PENDING" },
    { label: "Approved", count: counts.APPROVED, color: "#ec10c7ff", status: "APPROVED" },
    { label: "Rejected", count: counts.REJECTED, color: "#10a6ecff", status: "REJECTED" },
    { label: "Deactivate", count: counts.DEACTIVATED, color: "#ff0000", status: "DEACTIVATED" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <ArgonTypography variant="h6" fontWeight="medium">
                  All Tutors
                </ArgonTypography>
                <ArgonButton
                  variant="gradient"
                  color="info"
                  size="small"
                  onClick={() => setBulkUploadDialog(true)}
                  startIcon={<UploadFileIcon />}
                  sx={{
                    height: "38px",
                    px: 2,
                    borderRadius: "8px",
                    boxShadow: "0 8px 18px rgba(23, 193, 232, 0.28)",
                    textTransform: "none",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  Bulk Upload
                </ArgonButton>
              </ArgonBox>

              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                {/* Left Tabs */}
                <ArgonBox sx={{ height: "36px" }}>
                  <ApnaTabs tabsData={tabsData} onTabChange={handleTabChange} />
                </ArgonBox>

                <ArgonBox sx={{ height: "36px" }} display="flex" alignItems="center" gap={1}>
                  <ArgonInput
                    placeholder="search tutors..."
                    name="search"
                    size="small"
                    type="text"
                    variant="outlined"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                  {/* <ArrowCircleDownIcon
                    sx={{
                      fontSize: "38px !important",
                      color: "#1976d2",
                      padding: "8px",
                      backgroundColor: "#e3f2fd",
                      borderRadius: "50%",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#bbdefb",
                        transform: "scale(1.1)",
                      },
                    }}
                  /> */}
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching tutors...
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the latest data.
                  </ArgonTypography>
                </ArgonBox>
              ) : filteredRows.length > 0 ? (
                <Table columns={columns} rows={paginatedRows} />
              ) : (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    {searchQuery ? "No tutors found matching your search" : "No tutors found"}
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    {searchQuery
                      ? "Try adjusting your search terms or filters."
                      : "Try adjusting the filters or check back later."}
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>

            {/* Pagination Controls */}
            {filteredRows.length > 0 && (
              <PaginationBar
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalItems={filteredRows.length}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Card>
        </ArgonBox>

        {/* dialog popup */}
        <DeleteDialog
          open={deleteDialog}
          onClose={handleCloseDialog}
          onConfirm={handleDeleteConfirm}
          title="Delete Tutor"
          description="Are you sure you want to delete this tutor?"
          confirmText="Delete Tutor"
          cancelText="Cancel"
        />
        <CsvBulkUploadDialog
          open={bulkUploadDialog}
          onClose={() => setBulkUploadDialog(false)}
          title="Tutor Bulk Upload"
          endpoint="/tutor/bulk-upload/csv"
          description="Upload tutor CSV using the tutor bulk format. Required file field name is csvFile."
          templateUrl={`${process.env.PUBLIC_URL}/templates/tutor_bulk_upload_template.csv`}
          onSuccess={refetchTutors}
        />
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;

